'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { formInputBaseClass } from '@/components/form/FormInput';
import { useLocationFinder } from '@/lib/useLocationFinder';
import {
  meetsThreshold,
  useLocationSearch,
  type LocationSearchResult,
} from '@/lib/useLocationSearch';

export interface ZipCityLocation {
  slug: string;
  name: string;
  postcode?: string;
  /** `Locations.shNumber` — see `LocationSearchResult.centerNumber`. */
  centerNumber?: number;
}

export function locationLabel(loc: ZipCityLocation): string {
  return loc.postcode ? `${loc.postcode} - ${loc.name}` : loc.name;
}

function appendRequiredMark(text: string, required?: boolean): string {
  return required && !text.trimEnd().endsWith('*') ? `${text} *` : text;
}

function toZipCityLocation(r: LocationSearchResult): ZipCityLocation {
  return {
    slug: r.slug,
    name: r.name,
    postcode: r.postcode,
    centerNumber: r.centerNumber,
  };
}

// ── ZipCity ────────────────────────────────────────────────────────────────────
// Controlled autocomplete input with dropdown. Used standalone in forms.

export interface ZipCityProps {
  value: string;
  onValueChange: (value: string) => void;
  onSelect?: (loc: ZipCityLocation) => void;
  onSubmit?: (query: string, results: ZipCityLocation[]) => void;
  /**
   * Reports the dropdown's current (post-filter) suggestions on every change,
   * so a caller with its own submit control outside this component — the
   * separate `<Button>` in {@link ZipCityButton} — can submit with the same
   * results the visitor is actually looking at, rather than an empty array or
   * a stale guess from an earlier search.
   */
  onResultsChange?: (results: ZipCityLocation[]) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  /**
   * Keeps the suggestion list closed until the first keystroke. For a field
   * whose value was seeded rather than typed — the search results page
   * prefills the submitted term — where opening on mount would drop the
   * dropdown over the page's own content.
   */
  /**
   * The term the page seeded the field with. A seeded value is a search the
   * visitor already ran, not something they are typing, so the suggestion list
   * starts closed. Changing it closes the list again — the search page
   * navigates in place, so this component is never remounted between two
   * searches and a list left open would hang over the new results.
   */
  seededQuery?: string;
  inputClassName?: string;
  /**
   * `formField` sizes the input exactly like the shared `<FormInput />`
   * (h-55, text-sm, px-4 — via its exported `formInputBaseClass`) and
   * compacts the dropdown options to match, for use inside forms next to
   * other FormInput-based fields (see `ZipCityField`). `default` keeps the
   * larger standalone-search look. Ignored for the input when an explicit
   * `inputClassName` is passed.
   */
  variant?: 'default' | 'formField';
  /**
   * Whether to render `error` as inline text below the input. Default `true`.
   * Set `false` when the caller shows the message itself (e.g. in a summary
   * box above the field, as the tutor application form does) — `error` still
   * drives the red border and `aria-invalid`/`aria-describedby`.
   */
  showErrorText?: boolean;
  /**
   * Id of an externally rendered message, used for `aria-describedby` when
   * `showErrorText` is `false`. Without it the input points at an element that
   * is never rendered. Mirrors `FormInput`'s prop of the same name.
   */
  errorId?: string;
  /**
   * Replaces the permanent "Online Nachhilfe testen!" entry with a
   * non-interactive "no results" row carrying this text, shown only when the
   * search returns nothing.
   *
   * Set by forms where sending the visitor to `/online-nachhilfe` mid-flow
   * would abandon their input (see the tutor application form). Omit to keep
   * the default standalone-search behaviour of 10.03.03.13 AC 3.
   */
  noResultsText?: string;
  /**
   * Narrows the dropdown to the results this caller can actually use. A
   * filtered-out Standort is not rendered at all, so it cannot be selected by
   * click or keyboard, and an all-filtered search falls back to
   * `noResultsText` like an empty one.
   *
   * Used by the tutor application form, which can only submit a Standort that
   * has a `centerNumber`. Omit to offer every result.
   */
  filterResults?: (loc: ZipCityLocation) => boolean;
}

const formInputClass =
  'block h-[50px] w-full rounded-input border bg-[#eae8e7] px-5 py-3 text-base text-foreground outline-none placeholder:text-foreground/60 duration-100 focus:border-[rgba(40,25,10,0.2)]';

const ONLINE_NACHHILFE_HREF = '/online-nachhilfe';

export const ZipCity = ({
  value,
  onValueChange,
  onSelect,
  onSubmit,
  onResultsChange,
  onBlur,
  error,
  required,
  placeholder,
  seededQuery,
  inputClassName,
  variant = 'default',
  showErrorText = true,
  errorId: externalErrorId,
  noResultsText,
  filterResults,
}: ZipCityProps) => {
  const id = useId();
  const t = useTranslations('locationSearch');
  const errorId = error ? (externalErrorId ?? `${id}-error`) : undefined;
  const listboxId = `${id}-listbox`;
  const labelId = `${id}-label`;
  const [dismissed, setDismissed] = useState(Boolean(seededQuery));
  // Keyed on the term, not on `Boolean(term)`: going from one non-empty search
  // to another leaves the boolean unchanged while still needing the reset.
  const [seed, setSeed] = useState(seededQuery);
  if (seededQuery !== seed) {
    setSeed(seededQuery);
    setDismissed(Boolean(seededQuery));
  }
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);

  const { results, isLoading } = useLocationSearch(value);
  const mapped = results.map(toZipCityLocation);
  const suggestions = filterResults ? mapped.filter(filterResults) : mapped;
  const open = meetsThreshold(value) && !dismissed;
  // `results` lags behind `value` for up to the search hook's own debounce
  // plus request latency — `isLoading` is what tells the two apart. A
  // *decision* made from `suggestions` (submitting, or reporting them to a
  // caller that might submit) must not trust them while a fetch for the
  // current `value` is still in flight, or it risks submitting a match from
  // whatever the visitor typed a moment ago. The dropdown's own *rendering*
  // is unaffected — it still shows the previous list until fresh data
  // arrives, which is the existing, deliberate debounced-UI behaviour.
  const submittableSuggestions = isLoading ? [] : suggestions;

  useEffect(() => {
    setHighlightIndex(-1);
  }, [value]);

  // Runs every render rather than depending on `results` — a dependency array
  // only helps if `results` is referentially stable between renders that carry
  // the same data, which `useLocationSearch` itself is but a test double isn't
  // guaranteed to be (one widget's mock returns a fresh `[]` literal on every
  // call). Comparing slugs by hand instead of trusting reference identity
  // means a report-only-on-change caller (`setState`) can't be driven into a
  // render loop by either shape of `results`.
  const lastReportedRef = useRef<ZipCityLocation[] | null>(null);
  useEffect(() => {
    const last = lastReportedRef.current;
    const unchanged =
      last !== null &&
      last.length === submittableSuggestions.length &&
      last.every((loc, i) => loc.slug === submittableSuggestions[i]?.slug);
    if (unchanged) {
      return;
    }
    lastReportedRef.current = submittableSuggestions;
    onResultsChange?.(submittableSuggestions);
  });

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setDismissed(true);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const handleSelect = (loc: ZipCityLocation) => {
    onValueChange(locationLabel(loc));
    onSelect?.(loc);
    setDismissed(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setDismissed(true);
      return;
    }

    if (!open) {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmit?.(value, submittableSuggestions);
      }
      return;
    }

    // The trailing online-nachhilfe entry is selectable; the "no results" row
    // that replaces it is not, so it is excluded from the count.
    const totalOptions =
      suggestions.length + (noResultsText === undefined ? 1 : 0);
    // With a `noResultsText` and no suggestions there is nothing selectable, so
    // the arrow keys must stay inert — otherwise the highlight would move to an
    // option that is never rendered and `aria-activedescendant` would point at
    // a missing id.
    if (totalOptions === 0) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev < totalOptions - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : totalOptions - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
        handleSelect(suggestions[highlightIndex]);
      } else if (
        noResultsText === undefined &&
        highlightIndex === suggestions.length
      ) {
        window.location.href = ONLINE_NACHHILFE_HREF;
      } else {
        onSubmit?.(value, submittableSuggestions);
        setDismissed(true);
      }
    }
  };

  const isFormField = variant === 'formField';
  const inputClass =
    inputClassName ??
    `${isFormField ? formInputBaseClass : formInputClass} ${error ? 'border-red-400' : 'border-[#eae8e7]'}`;
  const optionClass = isFormField ? 'px-4 py-2 text-sm' : 'px-4 py-3';

  return (
    <div ref={ref} className="relative">
      <label id={labelId} htmlFor={`${id}-input`} className="sr-only">
        {placeholder ?? t('ariaLabel')}
      </label>
      <input
        id={`${id}-input`}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={
          open && highlightIndex >= 0
            ? `${id}-option-${highlightIndex}`
            : undefined
        }
        aria-labelledby={labelId}
        aria-invalid={Boolean(error)}
        aria-describedby={errorId}
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value);
          setDismissed(false);
        }}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        // No second star when the configured placeholder already carries one
        // (the seeded form fields end required placeholders with " *").
        placeholder={appendRequiredMark(
          placeholder ?? t('placeholder'),
          required,
        )}
        className={inputClass}
        autoComplete="off"
      />
      {error && showErrorText && (
        <p id={errorId} className="m-0 mt-1 pl-4 text-xs text-red-500">
          {error}
        </p>
      )}
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          // z-30: the dropdown overflows its widget, so it has to paint over
          // whatever follows it on the page. Section containers and widget
          // decorations (hero overlays, price badges) sit at z-10/z-20; the
          // fixed page chrome (header, sticky CTA bar) stays above at z-50.
          className="list absolute right-0 left-0 z-30 list-none divide-y divide-gray-200 overflow-hidden rounded-xl bg-white pl-0 shadow-lg"
        >
          {suggestions.map((loc, i) => (
            <li
              key={`${loc.slug}-${i}`}
              id={`${id}-option-${i}`}
              role="option"
              aria-selected={i === highlightIndex}
            >
              <button
                type="button"
                onClick={() => handleSelect(loc)}
                className={`hover:bg-card-bg w-full text-left ${optionClass} ${i === highlightIndex ? 'bg-card-bg' : ''}`}
              >
                {locationLabel(loc)}
              </button>
            </li>
          ))}
          {noResultsText === undefined ? (
            <li
              id={`${id}-option-${suggestions.length}`}
              role="option"
              aria-selected={suggestions.length === highlightIndex}
            >
              <a
                href={ONLINE_NACHHILFE_HREF}
                className={`hover:bg-card-bg block w-full text-left ${optionClass} ${suggestions.length === highlightIndex ? 'bg-card-bg' : ''}`}
              >
                {t('onlineNachhilfe')}
              </a>
            </li>
          ) : (
            suggestions.length === 0 && (
              // Not a `role="option"`: nothing here is selectable, so it must
              // not be announced or reachable as a choice.
              <li aria-disabled className={`text-foreground/60 ${optionClass}`}>
                {noResultsText}
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
};

// ── ZipCityButton ──────────────────────────────────────────────────────────────
// ZipCity + a Button. Navigation logic lives in useLocationFinder().

export interface ZipCityButtonProps {
  placeholder?: string;
  buttonLabel: string;
  anchor?: string;
  /** Prefills the input — see `useLocationFinder`. */
  initialQuery?: string;
}

const searchInputClass =
  'h-[50px] w-full rounded-full bg-[#eae8e7] px-5 text-base outline-none placeholder:text-foreground/50 focus:ring-2 focus:ring-primary/20 lg:w-xs';

export const ZipCityButton = ({
  placeholder,
  buttonLabel,
  anchor,
  initialQuery,
}: ZipCityButtonProps) => {
  const { query, handleValueChange, handleSelect, handleSubmit } =
    useLocationFinder(anchor, initialQuery);
  // The separate Button below sits outside `<ZipCity>`, so it has no access
  // to the dropdown's own live results — `onResultsChange` is how it stays
  // current with what the visitor is actually looking at.
  const [liveResults, setLiveResults] = useState<ZipCityLocation[]>([]);

  return (
    <div className="flex flex-col justify-center gap-3 md:flex-row">
      <div className="relative max-w-xl">
        <ZipCity
          value={query}
          onValueChange={handleValueChange}
          onSelect={handleSelect}
          onSubmit={handleSubmit}
          onResultsChange={setLiveResults}
          placeholder={placeholder}
          seededQuery={initialQuery}
          inputClassName={searchInputClass}
        />
      </div>
      <Button
        variant="primary"
        onClick={() => handleSubmit(query, liveResults)}
        className="w-full text-lg md:w-auto lg:w-xs"
      >
        {buttonLabel}
      </Button>
    </div>
  );
};
